import React, { useState, useEffect } from "react";
import { Container, Badge, Spinner, Row, Col, Button } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaCalendarAlt, FaGraduationCap, FaBookOpen, 
  FaChevronRight, FaRegDotCircle, FaExternalLinkAlt 
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useProgramData from "../hooks/useProgramData";

const SummerProgram = () => {
  const { data: programArray, loading } = useProgramData("summerProgramData");
  const [activeTab, setActiveTab] = useState("overview"); // 'overview' or category index
  
  const [programData, setProgramData] = useState({
    importantDates: { lastDateToApply: "", commencementOfClasses: "", courseFee: "" },
    notes: [],
  });

  useEffect(() => {
    if (programArray && programArray.length > 0) {
      setProgramData(programArray[0]);
    }
  }, [programArray]);

  // Static course data (Matching your accordion categories)
  const courseCategories = [
    { name: "Accounting", courses: ["Principles of Accounting-I", "Principles of Accounting-II", "Financial Accounting-I", "Managerial Accounting", "Corporate Accounting"] },
    { name: "Management", courses: ["Principles of Management", "Change Management", "Organizational Behavior", "Strategic Management", "Entrepreneurship"] },
    { name: "Finance", courses: ["Business Finance", "Financial Management", "Corporate Finance", "Portfolio Management", "Security Analysis"] },
    { name: "Marketing", courses: ["Principles of Marketing", "Brand Management", "Consumer Behavior", "Service Marketing", "Social Marketing"] },
    { name: "Computer Science", courses: ["Computer Architecture", "Database Management Systems", "Data Science", "Software Engineering", "Web Engineering"] },
    { name: "Mathematics & Stats", courses: ["Calculus-I", "Linear Algebra", "Business Maths", "Numerical Analysis", "Discrete Structures"] },
    { name: "English & Comm", courses: ["Business Communication", "Speech Communication", "English Grammar", "Technical Writing"] }
  ];

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2">PREPARING SUMMER PORTAL</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Hero Sub-Nav */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <h1 className="h4 fw-900 text-navy m-0">Summer <span className="text-primary">Program 2026</span></h1>
            </Col>
            <Col md={6} className="text-md-end">
              <Button 
                variant="primary" 
                href="http://applyadmission.iba-suk.edu.pk" 
                target="_blank"
                className="rounded-pill px-4 fw-bold shadow-sm"
              >
                Apply Online <FaExternalLinkAlt className="ms-2" size={12} />
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="portal-viewport py-5 bg-light-soft">
        <Container>
          <Row className="g-5">
            {/* Sidebar Navigation */}
            <Col lg={4}>
              <div className="sticky-sidebar" style={{ top: '120px' }}>
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">PROGRAM NAVIGATION</div>
                  <div className="program-list">
                    <button 
                      className={`prog-item ${activeTab === 'overview' ? 'is-active' : ''}`} 
                      onClick={() => setActiveTab('overview')}
                    >
                      <div className="prog-info">
                        <span className="name">Overview & Dates</span>
                        <span className="meta">Important Deadlines</span>
                      </div>
                      <FaChevronRight className="chevron" />
                    </button>
                    
                    <div className="card-label mt-2 px-4 py-2 bg-light small fw-bold text-muted">COURSE CATEGORIES</div>
                    {courseCategories.map((cat, i) => (
                      <button 
                        key={i} 
                        className={`prog-item ${activeTab === i ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab(i)}
                      >
                        <div className="prog-info">
                          <span className="name">{cat.name}</span>
                          <span className="meta">{cat.courses.length} Courses Offered</span>
                        </div>
                        <FaChevronRight className="chevron" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Col>

            {/* Main Content Area */}
            <Col lg={8}>
              <AnimatePresence mode="wait">
                {activeTab === 'overview' ? (
                  <motion.div 
                    key="overview" 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, y: -20 }}
                  >
                    {/* Header Card */}
                    <div className="mb-4">
                      <Badge bg="warning" text="dark" className="mb-2 px-3">AACSB ACCREDITED</Badge>
                      <h2 className="display-6 fw-bold text-navy">Academic Excellence</h2>
                      <p className="lead text-muted">The First Public Sector AACSB accredited University of Pakistan.</p>
                    </div>

                    {/* Important Dates Block */}
                    <div className="phase-block bg-white p-4 p-lg-5 rounded-4 shadow-sm mb-4 border-top border-primary border-4">
                      <h3 className="h5 fw-800 text-primary mb-4 text-uppercase">Timeline & Fees</h3>
                      <div className="timeline-v-stack">
                        <div className="milestone-row d-flex justify-content-between align-items-center py-3 border-bottom-dashed">
                          <div className="d-flex align-items-center">
                            <FaCalendarAlt className="me-3 text-primary" />
                            <span className="fw-700 text-navy">Application Deadline</span>
                          </div>
                          <span className="date-display">{programData.importantDates.lastDateToApply || "TBA"}</span>
                        </div>
                        <div className="milestone-row d-flex justify-content-between align-items-center py-3 border-bottom-dashed">
                          <div className="d-flex align-items-center">
                            <FaGraduationCap className="me-3 text-primary" />
                            <span className="fw-700 text-navy">Class Commencement</span>
                          </div>
                          <span className="date-display">{programData.importantDates.commencementOfClasses || "TBA"}</span>
                        </div>
                        <div className="milestone-row d-flex justify-content-between align-items-center py-3 border-bottom-dashed">
                          <div className="d-flex align-items-center">
                            <FaBookOpen className="me-3 text-primary" />
                            <span className="fw-700 text-navy">Course Fee</span>
                          </div>
                          <span className="date-display bg-success text-white">PKR {programData.importantDates.courseFee || "TBA"}</span>
                        </div>
                      </div>
                    </div>

                    {/* About Section */}
                    <div className="bg-white p-4 rounded-4 shadow-sm mb-4">
                      <h4 className="text-navy fw-bold mb-3">About the Program</h4>
                      <p className="text-muted">Sukkur IBA University offers a unique opportunity to enhance your skills and knowledge. Designed for business executives, managers, and students.</p>
                      <ul className="list-unstyled">
                        <li className="mb-2"><FaRegDotCircle className="text-primary me-2"/> Students from HEC-recognized institutions</li>
                        <li className="mb-2"><FaRegDotCircle className="text-primary me-2"/> Professionals and Industry Experts</li>
                      </ul>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div 
                    key={activeTab} 
                    initial={{ opacity: 0, x: 20 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="mb-4">
                      <h2 className="display-6 fw-bold text-navy">{courseCategories[activeTab].name}</h2>
                      <p className="text-muted">Specialized courses offered in the Summer session.</p>
                    </div>

                    <div className="bg-white p-4 p-lg-5 rounded-4 shadow-sm border-top border-primary border-4">
                       <Row>
                         {courseCategories[activeTab].courses.map((course, cIdx) => (
                           <Col md={6} key={cIdx} className="mb-3">
                             <div className="p-3 border rounded-3 bg-light-soft h-100 d-flex align-items-center">
                               <FaBookOpen className="text-primary me-3" />
                               <span className="fw-600 text-navy">{course}</span>
                             </div>
                           </Col>
                         ))}
                       </Row>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      <Footer />

      {/* Styled JSX (Keeping it consistent with Admission page) */}
      <style>{`
        .bg-light-soft { background: #f9fbfd; }
        .text-navy { color: #002b5a; }
        .fw-900 { font-weight: 900; }
        .fw-800 { font-weight: 800; }
        .fw-700 { font-weight: 700; }
        .fw-600 { font-weight: 600; }
        .letter-spacing-2 { letter-spacing: 2px; }
        
        .premium-loader { height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; }

        .sidebar-card { background: #fff; border-radius: 20px; overflow: hidden; }
        .prog-item {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          padding: 18px 25px; border: none; background: white; border-bottom: 1px solid #f1f5f9;
          text-align: left; transition: all 0.2s;
        }
        .prog-item.is-active { background: #002b5a; color: white; border-left: 6px solid #ffb800; }
        .prog-item:hover:not(.is-active) { background: #f8f9fa; }
        
        .prog-info .name { display: block; font-weight: 700; font-size: 0.95rem; }
        .prog-info .meta { font-size: 0.75rem; opacity: 0.7; }
        
        .milestone-row { transition: 0.2s; }
        .date-display {
          background: #f0f4f8; color: #0055a4; padding: 5px 15px;
          border-radius: 50px; font-weight: 800; font-size: 0.85rem;
        }

        .border-bottom-dashed { border-bottom: 1px dashed #e2e8f0; }
        
        @media (max-width: 991px) {
          .program-list { display: flex; overflow-x: auto; padding: 15px; gap: 10px; }
          .prog-item { width: auto; border-radius: 12px; border: 1px solid #e2e8f0; white-space: nowrap; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default SummerProgram;